import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { Navbar } from '../components/Navbar'
import { SiteFooter } from '../sections/SiteFooter'
import { FadeIn } from '../components/ui/FadeIn'
import { GlassCard } from '../components/ui/GlassCard'
import { Shield, Lock, Key, Eye, Server } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

export function SecurityPage() {
  const { t } = useLanguage()
  
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const securityFeatures = [
    {
      icon: Lock,
      titleKey: 'legal.security.encryption.title',
      descKey: 'legal.security.encryption.desc',
    },
    {
      icon: Key,
      titleKey: 'legal.security.auth.title',
      descKey: 'legal.security.auth.desc',
    },
    {
      icon: Eye,
      titleKey: 'legal.security.monitoring.title',
      descKey: 'legal.security.monitoring.desc',
    },
    {
      icon: Server,
      titleKey: 'legal.security.infrastructure.title',
      descKey: 'legal.security.infrastructure.desc',
    },
  ]

  return (
    <div className="min-h-screen bg-nexus-black text-white">
      <Navbar />
      <main className="pt-16 pb-16 sm:pt-20 lg:pt-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="grid size-12 place-items-center rounded-xl bg-white/10 border border-white/10">
                <Shield className="size-6 text-nexus-blue" />
              </div>
              <div>
                <div className="text-xs font-semibold tracking-[0.2em] text-nexus-blue/80">
                  {t('legal.security.label')}
                </div>
                <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
                  {t('legal.security.title')}
                </h1>
              </div>
            </div>
            <p className="text-white/70 mt-4 max-w-2xl">
              {t('legal.security.description')}
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 mb-8">
            {securityFeatures.map((feature, index) => {
              const Icon = feature.icon
              return (
                <FadeIn key={feature.titleKey} delay={0.1 * index}>
                  <GlassCard>
                    <div className="flex items-start gap-4">
                      <div className="grid size-12 place-items-center rounded-xl bg-white/10 border border-white/10 flex-shrink-0">
                        <Icon className="size-6 text-nexus-blue" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2">{t(feature.titleKey)}</h3>
                        <p className="text-white/70 text-sm leading-relaxed">
                          {t(feature.descKey)}
                        </p>
                      </div>
                    </div>
                  </GlassCard>
                </FadeIn>
              )
            })}
          </div>

          <FadeIn delay={0.4}>
            <GlassCard>
              <h2 className="text-xl font-semibold mb-4">{t('legal.security.commitment.title')}</h2>
              <div className="text-white/70 space-y-3 leading-relaxed">
                {t('legal.security.commitment.content')
                  .split('\n')
                  .map((paragraph, pIndex) => (
                    <p key={pIndex}>{paragraph}</p>
                  ))}
              </div>
            </GlassCard>
          </FadeIn>
        </div>
      </main>
      <SiteFooter />
    </div>
  )
}

